import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import ScoreIcon from '@mui/icons-material/Score';
import { useTriggerScoring } from './hooks/useTriggerScoring';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { ScoringMode } from './types';
import ScoringBatchesTable from './components/ScoringBatchesTable';
import MissingResumeIdPanel from './components/MissingResumeIdPanel';
import BatchScoreRetryPanel from './components/BatchScoreRetryPanel';

const MODE_LABELS: Record<ScoringMode, string> = {
    many_to_one: 'Talents sélectionnés × une offre (many_to_one)',
    one_to_many: 'Un talent × plusieurs offres (one_to_many)',
    all_to_one: 'Tous les candidats × une offre (all_to_one)',
    one_to_all: 'Un talent × toutes les offres (one_to_all)',
    one_to_one: 'Un talent × une offre (one_to_one)',
};

export const ScoringDashboardModule = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { triggerScoring, loading } = useTriggerScoring();

    const [mode, setMode] = useState<ScoringMode>('all_to_one');
    const [jobOfferId, setJobOfferId] = useState('');
    const [talentId, setTalentId] = useState('');
    const [formError, setFormError] = useState<string | null>(null);

    const needsJobOffer = mode === 'all_to_one' || mode === 'many_to_one' || mode === 'one_to_one';
    const needsTalent = mode === 'one_to_many' || mode === 'one_to_all' || mode === 'many_to_one' || mode === 'one_to_one';

    const handleSubmit = async () => {
        setFormError(null);
        if (needsJobOffer && !jobOfferId.trim()) {
            setFormError('L\'identifiant de l\'offre est requis pour ce mode.');
            return;
        }
        if (needsTalent && !talentId.trim()) {
            setFormError('L\'identifiant du talent est requis pour ce mode.');
            return;
        }

        const input = {
            mode,
            jobOfferId: needsJobOffer ? jobOfferId.trim() : undefined,
            targets: needsTalent ? [{ talentId: talentId.trim() }] : undefined,
        };

        const result = await triggerScoring(input);
        if (!result) {
            showSnackbar('Erreur lors du déclenchement du scoring', 'error');
            return;
        }

        if (result.totalQueued === 0) {
            const reasons = result.validationWarnings.map(w => w.reason).join(', ');
            showSnackbar(`Aucun élément mis en file : ${reasons || 'validation échouée'}`, 'warning');
            return;
        }

        navigate(`/scoring/batch/${result.batchId}`);
    };

    return (
        <Box>
            {/* Trigger form */}
            <Box
                sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.border.main}`,
                    backgroundColor: theme.palette.background.paper,
                    mb: 4,
                    maxWidth: 720,
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Nouveau scoring
                </Typography>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Mode</InputLabel>
                    <Select
                        value={mode}
                        label="Mode"
                        onChange={(e) => {
                            setMode(e.target.value as ScoringMode);
                            setFormError(null);
                        }}
                    >
                        {(Object.keys(MODE_LABELS) as ScoringMode[]).map((m) => (
                            <MenuItem key={m} value={m}>
                                {MODE_LABELS[m]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {needsJobOffer && (
                    <TextField
                        label="ID de l'offre d'emploi"
                        value={jobOfferId}
                        onChange={(e) => setJobOfferId(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                        placeholder="ex: 6641f3b2c..."
                    />
                )}

                {needsTalent && (
                    <TextField
                        label="ID du talent"
                        value={talentId}
                        onChange={(e) => setTalentId(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                        placeholder="ex: 6641f3b2c..."
                    />
                )}

                {formError && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {formError}
                    </Alert>
                )}

                <Button
                    variant="contained"
                    startIcon={<ScoreIcon />}
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    Lancer le scoring
                </Button>
            </Box>

            {/* All batches */}
            <Box>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Historique des batches
                </Typography>
                <ScoringBatchesTable />
            </Box>

            {/* Batch repair for missing resumeIds */}
            <Box>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Réparation des CVs non analysés
                </Typography>
                <MissingResumeIdPanel />
            </Box>

            {/* Batch retry for errored job applications */}
            <Box>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Réessai des candidatures en erreur
                </Typography>
                <BatchScoreRetryPanel />
            </Box>
        </Box>
    );
};

export default ScoringDashboardModule;
